import type {
  FieldRegistry,
  ResolvedField,
  ResolvedOption,
  StaticResolvedField,
  DynamicResolvedField,
  BooleanResolvedField,
  DateResolvedField,
  PassThroughResolvedField,
  OverviewFieldDescriptor,
} from './types';
import type {
  FieldMetadata,
  Lookup as LookupEntry,
} from '../../contexts/MetadataContext';

export interface BuildFieldRegistryDeps {
  /** Look up FieldMetadata by Key (e.g., 'MLS_STATUS') */
  getFieldMetadata: (key: string) => FieldMetadata | undefined;

  /** Look up entries for a lookup table (e.g., 'Locale_Listing_Statuses') */
  lookup: (key: string) => LookupEntry[] | undefined;

  /** Resolve a raw value to its display string via lookup */
  displayValueForListingField: (lookupKey: string, value: any) => string;

  /** Async search for dynamic/cascading lookups */
  searchLookups: (
    lookupName: string,
    parentLookupName: string,
    parentLookupValue: string,
  ) => Promise<any[]>;

}

/**
 * Builds a FieldRegistry from a declarative array of field descriptors.
 *
 * For each descriptor, reads the FieldMetadata to auto-detect resolution type:
 *   - dynamicLookup present → 'dynamic'
 *   - Lookup present        → 'static'
 *   - neither               → 'none'
 */
export function buildFieldRegistry(
  fields: OverviewFieldDescriptor[],
  deps: BuildFieldRegistryDeps,
): FieldRegistry {
  const registry = new Map<string, ResolvedField>();

  for (const descriptor of fields) {
    const meta = deps.getFieldMetadata(descriptor.fieldKey);

    let resolved: ResolvedField;

    if (descriptor.type === 'boolean') {
      resolved = buildBooleanField(descriptor);
    } else if (descriptor.type === 'date') {
      resolved = buildDateField(descriptor);
    } else if (meta?.dynamicLookup) {
      resolved = buildDynamicField(descriptor, meta, deps);
    } else if (meta?.Lookup) {
      resolved = buildStaticField(descriptor, meta, deps);
    } else {
      resolved = buildPassThroughField(descriptor);
    }

    registry.set(descriptor.fieldKey, resolved);
  }

  return registry;
}

// ── Builder helpers ────────────────────────────────────────────────────

function buildStaticField(
  descriptor: OverviewFieldDescriptor,
  meta: FieldMetadata,
  deps: BuildFieldRegistryDeps,
): StaticResolvedField {
  const lookupKey = meta.Lookup!;
  const rawEntries = deps.lookup(lookupKey) || [];

  const transformLabel = descriptor.transformLabel;
  const resolvedEntries: ResolvedOption[] = rawEntries.map((entry) => {
    const rawLabel = entry.DisplayValue || entry.LookupValue;
    return {
      value: entry.LookupValue,
      label: transformLabel ? transformLabel(rawLabel) : rawLabel,
    };
  });

  const options: ResolvedOption[] = [
    { value: 'all', label: descriptor.allLabel ?? `All ${descriptor.label}` },
    ...resolvedEntries,
  ];

  const optionMap = new Map(resolvedEntries.map((o) => [o.value, o]));

  return {
    fieldKey: descriptor.fieldKey,
    label: descriptor.label,
    type: 'static',
    filterable: descriptor.filterable ?? false,
    section: descriptor.section,
    disabled: descriptor.disabled ?? false,
    options,
    resolve: (rawValue: string) =>
      optionMap.get(rawValue) || { value: rawValue, label: rawValue },
  };
}

function buildDynamicField(
  descriptor: OverviewFieldDescriptor,
  meta: FieldMetadata,
  deps: BuildFieldRegistryDeps,
): DynamicResolvedField {
  const { dynamicLookup } = meta;
  const parentLocaleKey = dynamicLookup!.lookupParent;
  const standardName = dynamicLookup!.context.standardName;

  // Resolve the parent's field Key from its locale key
  const parentMeta = deps.getFieldMetadata(parentLocaleKey);
  const parentFieldKey = parentMeta?.Key || parentLocaleKey;

  return {
    fieldKey: descriptor.fieldKey,
    label: descriptor.label,
    type: 'dynamic',
    filterable: descriptor.filterable ?? false,
    section: descriptor.section,
    disabled: descriptor.disabled ?? false,
    parentFieldKey,
    fetchOptions: async (parentValue: string) => {
      const results = await deps.searchLookups(
        standardName,
        parentLocaleKey,
        parentValue,
      );
      return results.map((item: any) => ({
        value: item.LookupValue || item.value,
        label: item.DisplayValue || item.LookupValue || item.value,
      }));
    },
    resolve: (rawValue: string) => {
      // Synchronous best-effort: use displayValueForListingField if a Lookup exists
      if (meta.Lookup) {
        const display = deps.displayValueForListingField(meta.Lookup, rawValue);
        return { value: rawValue, label: display || rawValue };
      }
      return { value: rawValue, label: rawValue };
    },
  };
}

function buildBooleanField(
  descriptor: OverviewFieldDescriptor,
): BooleanResolvedField {
  return {
    fieldKey: descriptor.fieldKey,
    label: descriptor.label,
    type: 'boolean',
    filterable: descriptor.filterable ?? false,
    section: descriptor.section,
    disabled: descriptor.disabled ?? false,
  };
}

function buildDateField(
  descriptor: OverviewFieldDescriptor,
): DateResolvedField {
  return {
    fieldKey: descriptor.fieldKey,
    label: descriptor.label,
    type: 'date',
    filterable: descriptor.filterable ?? false,
    section: descriptor.section,
    disabled: descriptor.disabled ?? false,
  };
}

function buildPassThroughField(
  descriptor: OverviewFieldDescriptor,
): PassThroughResolvedField {
  return {
    fieldKey: descriptor.fieldKey,
    label: descriptor.label,
    type: 'none',
    filterable: descriptor.filterable ?? false,
    section: descriptor.section,
    disabled: descriptor.disabled ?? false,
  };
}

